/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-base-to-string */
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { MessageService } from "primeng/api";
import {
  QPilotService,
  QPilotAccessToken,
  ProductGroup,
} from "../../../core/services/qpilot.service";
import {
  IProductGroupListResponse,
  IProductGroupFilterParams,
  IProductSearchResult,
  IProductGroupRequest,
} from "../interfaces/product-group.interface";

/**
 * Product Groups Service
 * Provides high-level business logic for product group operations
 * Wraps QPilotService with error handling and user feedback
 *
 * NOTE: Client-side filtering/pagination as API returns all groups
 */
@Injectable({ providedIn: "root" })
export class ProductGroupsService {
  constructor(
    private readonly qpilotService: QPilotService,
    private readonly messageService: MessageService,
  ) {}

  /**
   * Get product groups for a site with filtering and pagination
   * Implements client-side filtering/pagination as API returns all groups
   */
  getProductGroups(
    siteId: number,
    accessToken: QPilotAccessToken,
    params: IProductGroupFilterParams = {},
    showGlobalLoader: boolean = false,
  ): Observable<IProductGroupListResponse> {
    const {
      search = "",
      page = 1,
      pageSize = 20,
      sortField,
      sortOrder,
    } = params;

    return this.qpilotService
      .getProductGroups(siteId, accessToken, showGlobalLoader)
      .pipe(
        map((groups) => {
          // Client-side filtering by search term (name or ID)
          let filtered = groups;
          if (search.trim()) {
            const searchLower = search.toLowerCase().trim();
            filtered = groups.filter(
              (g) =>
                g.Name?.toLowerCase().includes(searchLower) ||
                g.ProductGroupId?.toString().includes(searchLower),
            );
          }

          // Client-side sorting
          if (sortField) {
            filtered = [...filtered].sort((a, b) => {
              const aVal = a[sortField as keyof ProductGroup];
              const bVal = b[sortField as keyof ProductGroup];
              const comparison = String(aVal ?? "").localeCompare(
                String(bVal ?? ""),
              );
              return sortOrder === -1 ? -comparison : comparison;
            });
          }

          // Client-side pagination
          const totalRecords = filtered.length;
          const startIndex = (page - 1) * pageSize;
          const paginatedItems = filtered.slice(
            startIndex,
            startIndex + pageSize,
          );

          return {
            Items: paginatedItems,
            TotalRecords: totalRecords,
            Page: page,
            PageSize: pageSize,
          };
        }),
        catchError((error) => this.handleError(error, "load product groups")),
      );
  }

  /**
   * Get a single product group by ID
   */
  getProductGroup(
    siteId: number,
    id: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<ProductGroup> {
    return this.qpilotService
      .getProductGroup(siteId, id, accessToken, showGlobalLoader)
      .pipe(
        catchError((error) => this.handleError(error, "load product group")),
      );
  }

  /**
   * Create a new product group
   */
  createProductGroup(
    siteId: number,
    request: IProductGroupRequest,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<ProductGroup> {
    const productGroup: Partial<ProductGroup> = {
      Name: request.Name,
      SiteId: request.SiteId,
      Products: request.Products,
    };

    return this.qpilotService
      .createProductGroup(siteId, productGroup, accessToken, showGlobalLoader)
      .pipe(
        tap(() => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Product group created successfully",
            life: 3000,
          });
        }),
        catchError((error) => this.handleError(error, "create product group")),
      );
  }

  /**
   * Update an existing product group
   */
  updateProductGroup(
    siteId: number,
    productGroup: ProductGroup,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<ProductGroup> {
    return this.qpilotService
      .updateProductGroup(siteId, productGroup, accessToken, showGlobalLoader)
      .pipe(
        tap(() => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Product group updated successfully",
            life: 3000,
          });
        }),
        catchError((error) => this.handleError(error, "update product group")),
      );
  }

  /**
   * Delete a product group
   */
  deleteProductGroup(
    siteId: number,
    productGroupId: number,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<void> {
    return this.qpilotService
      .deleteProductGroup(siteId, productGroupId, accessToken, showGlobalLoader)
      .pipe(
        tap(() => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Product group deleted successfully",
            life: 3000,
          });
        }),
        catchError((error) => this.handleError(error, "delete product group")),
      );
  }

  /**
   * Delete multiple product groups
   */
  deleteProductGroups(
    siteId: number,
    productGroupIds: number[],
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<void[]> {
    const deleteObservables = productGroupIds.map((id) =>
      this.qpilotService
        .deleteProductGroup(siteId, id, accessToken, showGlobalLoader)
        .pipe(catchError(() => of(undefined as unknown as void))),
    );

    return new Observable((subscriber) => {
      Promise.all(deleteObservables.map((obs) => obs.toPromise()))
        .then((results) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `${productGroupIds.length} product group(s) deleted successfully`,
            life: 3000,
          });
          subscriber.next(results as void[]);
          subscriber.complete();
        })
        .catch((error) => {
          subscriber.error(error);
        });
    });
  }

  /**
   * Search products for autocomplete
   * Filters out variable type products
   */
  searchProducts(
    siteId: number,
    search: string,
    accessToken: QPilotAccessToken,
    showGlobalLoader: boolean = false,
  ): Observable<IProductSearchResult[]> {
    return this.qpilotService
      .getProductsForSite(siteId, accessToken, 1, search, showGlobalLoader)
      .pipe(
        map((products) =>
          products
            .filter((p) => p.Type !== "variable")
            .map((p) => ({
              Id: p.Id,
              ExternalId: p.ExternalId,
              Title: p.Title,
              Type: p.Type,
            })),
        ),
        catchError(() => of([])),
      );
  }

  private handleError(error: unknown, operation: string): Observable<never> {
    let errorMessage = `Failed to ${operation}`;

    if (error && typeof error === "object") {
      const errorObj = error as {
        error?: { message?: string; Message?: string };
        message?: string;
        Message?: string;
      };
      errorMessage =
        errorObj.error?.message ||
        errorObj.error?.Message ||
        errorObj.message ||
        errorObj.Message ||
        errorMessage;
    }

    this.messageService.add({
      severity: "error",
      summary: "Error",
      detail: errorMessage,
      life: 5000,
    });

    return throwError(() => new Error(errorMessage));
  }
}
